import { observer } from 'mobx-react-lite'
import controller from '../controllers/user.controller'
import i from '../../../translations/i'
import { Helmet } from 'react-helmet'
import { Button, Input } from 'antd'
import { AppstoreOutlined, LockOutlined, MailOutlined, SearchOutlined, SmileOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { config } from '../../../config/app'
import { AuthError, AuthField, AuthHead, AuthSwitch } from '../shell/AuthShell'
import { CardList, ListChip, ListToolbar, ListToolbarField } from '../../../components/card_list/CardList'
import { ProjectCard, projectMatches } from '../../../components/project_card/ProjectCard'

export const Login = observer(() => {

    const failed = controller.error !== ''

    return (
        <>
            <Helmet>
                <title>{i.t('auth:title.login')}</title>
            </Helmet>

            <AuthHead title={i.t('authShell:login.title')} text={i.t('authShell:login.text')} />

            <form className="au-form" onSubmit={(e) => controller.login(e)}>
                <AuthError message={controller.error} />

                <AuthField label={i.t('auth:placeholder.email')}>
                    <Input
                        size="large"
                        type="email"
                        autoComplete="email"
                        autoFocus
                        status={failed ? 'error' : undefined}
                        prefix={<MailOutlined />}
                        placeholder={i.t('authShell:emailPlaceholder')}
                        value={controller.dataLogin.login}
                        onChange={(e) => controller.onChangeLogin('login', e.target.value)}
                        required
                    />
                </AuthField>

                <AuthField label={i.t('auth:placeholder.password')} extra={<a href="/forgot">{i.t('auth:link.forgot')}</a>}>
                    <Input.Password
                        size="large"
                        autoComplete="current-password"
                        status={failed ? 'error' : undefined}
                        prefix={<LockOutlined />}
                        placeholder={i.t('authShell:passwordPlaceholder')}
                        value={controller.dataLogin.password}
                        onChange={(e) => controller.onChangeLogin('password', e.target.value)}
                        required
                    />
                </AuthField>

                <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    block
                    loading={controller.submitting}
                    disabled={failed}
                >
                    {i.t('def:actions.enter')}
                </Button>
            </form>

            <AuthSwitch text={i.t('authShell:login.noAccount')} link={i.t('authShell:login.register')} href="/register" />
        </>
    )
})

// Выбор проекта после входа: карточки с поиском и данными проекта, как «Все проекты» суперадминки.
// Если проектов ещё нет — экран ожидания с анкетой, а список тихо обновляется, пока менеджера не подключат к проекту.
// Экран ждёт выбора сколько угодно: раньше он раз в 30 с подменял токен на токен без проекта и уходил на главную
export const LoginSelectProject = observer(() => {
    const [search, setSearch] = useState('')

    // До ответа в списке лежит пустая заглушка без id
    const loaded = !(controller.projects.length === 1 && !controller.projects[0]?.id)
    const projects = controller.projects.filter((project: any) => project && project.id)
    const waiting = loaded && !projects.length

    useEffect(() => {
        controller.getProjectsByManager()
    }, [])

    useEffect(() => {
        if (!waiting) return

        const timer = setInterval(() => controller.getProjectsByManager(true), 30000)
        return () => clearInterval(timer)
    }, [waiting])

    if (waiting) {
        return (
            <>
                <AuthHead icon={<SmileOutlined />} title={`${i.t('wait:h1')}!`} text={i.t('wait:h2')} />
                <div className="au-form">
                    <p className="au-head__text">{i.t('wait:description')}</p>
                    <p className="au-head__text">{i.t('wait:form')}</p>
                    <Button size="large" type="primary" block href={config.formUrl} target="_blank" rel="noreferrer">
                        {i.t('wait:buttonForm')}
                    </Button>
                </div>
            </>
        )
    }

    const query = search.trim().toLowerCase()
    const shown = query ? projects.filter((project: any) => projectMatches(project, query)) : projects

    return (
        <div className="au-select">
            <AuthHead icon={<AppstoreOutlined />} title={i.t('authShell:select.title')} text={i.t('authShell:select.text')} />
            <CardList
                items={shown}
                loading={!loaded}
                // Одному проекту поиск ни к чему
                toolbar={projects.length > 1 &&
                    <ListToolbar>
                        <ListToolbarField wide>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder={i.t('superAdmin:projects.search')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </ListToolbarField>
                    </ListToolbar>
                }
                empty={{ icon: <AppstoreOutlined />, title: i.t('superAdmin:projects.empty') }}
                renderItem={(project: any) => (
                    <ProjectCard
                        project={project}
                        bundle={!project.seller}
                        chips={project.seller && <ListChip>{i.t('def:seller')}</ListChip>}
                        onOpen={() => controller.changeProject(project.id)}
                    />
                )}
            />
        </div>
    )
})
